import { useState } from 'react';
import { useList, useGo, useCustomMutation } from '@refinedev/core';
import { Check, X, Pencil, Plus, Minus, ToggleLeft, Edit3 } from 'lucide-react';
import { notify } from '@/lib/notify';
import { resolveApiUrl } from '@/providers/api-url';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FieldRow, FieldGrid, formatDate, formatCurrency, EmptyValue, MonoValue, LinkValue } from '@/components/universal';
import { cn } from '@/lib/utils';
import {
  PricingTierBadge,
  PricingTierInfoContent,
} from '@/components/shared/PricingTierBadge';
import type { PricingTier } from '@/components/shared/PricingTiersTooltip';
import { OrderHistory } from './OrderHistory';
import type { InferSelectModel } from 'drizzle-orm';
import type { order } from '@masters-club/db';

type OrderRecord = InferSelectModel<typeof order>;
// Looser record type accepted by section components — dates may be string (API) or Date (DB)
type OrderSectionRecord = Omit<OrderRecord, 'createdAt' | 'updatedAt' | 'deletedAt'> & {
  createdAt: Date | string;
  updatedAt: Date | string;
  deletedAt: Date | string | null;
};

interface OrderItemTargetInfo {
  name: string;
  price: string;
  currency: string;
}

interface BookingMetadata {
  adults?: number;
  children?: number;
  infants?: number;
  accommodationType?: 'SINGLE' | 'DOUBLE';
  isAccommodation?: boolean;
  accommodationName?: string;
}

interface PricingTierBonusInfo {
  id: number;
  type: string;
  name: string | null;
  description: string | null;
}

interface PricingTierInfoData {
  id: number;
  name: string | null;
  shortDescription: string | null;
  prices: Record<string, number>;
  baseCurrency: string;
  durationDays: number | null;
  gradientFrom: string | null;
  gradientTo: string | null;
  bonuses: PricingTierBonusInfo[];
}

interface OrderItemData {
  id: number;
  targetType: string;
  targetId: number;
  unitPrice: string;
  quantity: number;
  total: string;
  target: OrderItemTargetInfo | null;
  metadata?: BookingMetadata | null;
  pricingTierInfo?: PricingTierInfoData | null;
}

interface OrderWithRelations extends OrderSectionRecord {
  status?: string;
  items?: OrderItemData[];
  user?: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    email: string | null;
    phone: string | null;
    telegramId: string;
  };
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  COURSE: 'Курс',
  SUBSCRIPTION_PLAN: 'Абонемент',
  CHANNEL_PASS: 'Доступ к каналу',
  BONUS: 'Привилегия',
};

const ORDER_STATUS_MAP: Record<string, { label: string; className: string }> = {
  PAID: { label: 'Оплачен', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  CANCELLED: { label: 'Отменён', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
  PENDING: { label: 'Ожидает', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  REFUNDED: { label: 'Возврат', className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  DRAFT: { label: 'Черновик', className: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
};

const PAYMENT_STATUS_MAP: Record<string, { label: string; className: string }> = {
  CONFIRMED: { label: 'Подтверждён', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  REJECTED: { label: 'Отклонён', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
  PENDING: { label: 'Ожидает', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  REFUNDED: { label: 'Возврат', className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
};

function renderStatus(value: string | undefined | null, map: Record<string, { label: string; className: string }>) {
  if (!value) {return <Badge variant="outline" className="text-[11px] px-1.5 py-0">—</Badge>;}
  const config = map[value.toUpperCase()];
  return (
    <Badge variant="outline" className={cn('text-[11px] px-1.5 py-0', config?.className)}>
      {config?.label ?? value}
    </Badge>
  );
}

export function OrderMainSection({ record }: { record: OrderSectionRecord }) {
  const r = record as OrderWithRelations;

  return (
    <FieldGrid>
      <FieldRow label="ID"><MonoValue>#{r.id}</MonoValue></FieldRow>
      <FieldRow label="Статус">{renderStatus(r.status, ORDER_STATUS_MAP)}</FieldRow>
      <FieldRow label="Итого">
        <span className="font-mono font-semibold">{formatCurrency(r.total, r.currency)}</span>
      </FieldRow>
      <FieldRow label="Валюта">{r.currency}</FieldRow>
      <FieldRow label="Создан">{formatDate(r.createdAt)}</FieldRow>
      <FieldRow label="Обновлён">{formatDate(r.updatedAt)}</FieldRow>
    </FieldGrid>
  );
}

export function OrderUserSection({ record }: { record: OrderSectionRecord }) {
  const r = record as OrderWithRelations;
  const go = useGo();

  if (!r.user) {
    return (
      <FieldGrid>
        <FieldRow label="ID пользователя">
          <LinkValue onClick={() => go({ to: `/users/show/${r.userId}` })}>
            #{r.userId}
          </LinkValue>
        </FieldRow>
      </FieldGrid>
    );
  }

  const userName = [r.user.firstName, r.user.lastName].filter(Boolean).join(' ');

  return (
    <FieldGrid>
      <FieldRow label="ID пользователя">
        <LinkValue onClick={() => go({ to: `/users/show/${r.userId}` })}>
          #{r.userId}
        </LinkValue>
      </FieldRow>
      <FieldRow label="Имя">{userName || <EmptyValue />}</FieldRow>
      {r.user.username && <FieldRow label="Username">@{r.user.username}</FieldRow>}
      {r.user.phone && <FieldRow label="Телефон"><MonoValue>{r.user.phone}</MonoValue></FieldRow>}
      {r.user.email && <FieldRow label="Email">{r.user.email}</FieldRow>}
      {r.user.telegramId && <FieldRow label="Telegram ID"><MonoValue>{r.user.telegramId}</MonoValue></FieldRow>}
    </FieldGrid>
  );
}

const ACCOMMODATION_LABELS: Record<string, string> = {
  SINGLE: 'Одноместное',
  DOUBLE: 'Двухместное',
};

function BookingMetadataDisplay({ metadata }: { metadata: BookingMetadata }) {
  const parts: string[] = [];
  if (metadata.adults !== undefined && metadata.adults > 0) {
    parts.push(`Взрослых: ${metadata.adults}`);
  }
  if (metadata.children !== undefined && metadata.children > 0) {
    parts.push(`Детей: ${metadata.children}`);
  }
  if (metadata.infants !== undefined && metadata.infants > 0) {
    parts.push(`Младенцев: ${metadata.infants}`);
  }

  return (
    <div className="mt-2 rounded border border-border bg-muted/30 px-2.5 py-1.5">
      <p className="mb-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Бронирование</p>
      <div className="flex flex-wrap gap-1 text-xs">
        {parts.map((p) => (
          <Badge key={p} variant="secondary" className="text-[11px] px-1.5 py-0">{p}</Badge>
        ))}
        {metadata.accommodationType && (
          <Badge variant="outline" className="text-[11px] px-1.5 py-0">
            {ACCOMMODATION_LABELS[metadata.accommodationType] ?? metadata.accommodationType} размещение
          </Badge>
        )}
      </div>
    </div>
  );
}

/**
 * Адаптер урезанного PricingTierInfoData (ответ заказа) к полному
 * PricingTier — чтобы переиспользовать общий PricingTier-компонент.
 */
function toPricingTier(tier: PricingTierInfoData): PricingTier {
  return {
    id: tier.id,
    name: tier.name ?? undefined,
    prices: tier.prices,
    baseCurrency: tier.baseCurrency,
    durationDays: tier.durationDays,
    isActive: true,
    bonuses: tier.bonuses.map((b) => ({
      id: b.id,
      name: b.name ?? undefined,
      type: b.type,
    })),
  };
}

function PricingTierInfoDisplay({ tier }: { tier: PricingTierInfoData }) {
  return (
    <div className="mt-1.5 rounded border border-border bg-muted/30">
      <PricingTierInfoContent tier={toPricingTier(tier)} index={0} />
    </div>
  );
}

export function OrderItemsSection({ record, onRefresh }: { record: OrderSectionRecord; onRefresh?: () => void }) {
  const r = record as OrderWithRelations;
  const items = r.items ?? [];
  const { mutateAsync: updateCustom } = useCustomMutation<{ success: boolean }>();

  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editPrice, setEditPrice] = useState('');
  const { mutateAsync: bonusAction, mutation: bonusMutation } = useCustomMutation<{ success: boolean }>();
  const bonusActionPending = bonusMutation.isPending;
  const [editingBonusId, setEditingBonusId] = useState<number | null>(null);
  const [editBonusValue, setEditBonusValue] = useState('');

  const hasBonusItems = items.some((item) => item.targetType === 'BONUS');

  const handleBonusQuickAction = (bonusId: number, action: string) => {
    const apiUrl = resolveApiUrl();
    const needsValue = action === 'increment' || action === 'decrement';
    bonusAction(
      {
        url: `${apiUrl}/user-bonuses/admin/${bonusId}`,
        method: 'patch',
        values: {
          action,
          ...(needsValue ? { value: 1 } : { value: true }),
          idempotencyKey: crypto.randomUUID(),
        },
      },
      {
        onSuccess: () => {
          onRefresh?.();
          notify.success('Действие выполнено');
        },
        onError: () => {
          notify.error('Ошибка при выполнении действия');
        },
      },
    );
  };

  const handleBonusSetValue = (bonusId: number, bonusType: string) => {
    const apiUrl = resolveApiUrl();
    const parsedValue = bonusType === 'NUMBER' ? Number(editBonusValue) : editBonusValue;
    bonusAction(
      {
        url: `${apiUrl}/user-bonuses/admin/${bonusId}`,
        method: 'patch',
        values: {
          action: 'set',
          value: parsedValue,
          idempotencyKey: crypto.randomUUID(),
        },
      },
      {
        onSuccess: () => {
          setEditingBonusId(null);
          onRefresh?.();
          notify.success('Значение обновлено');
        },
        onError: () => {
          notify.error('Ошибка при обновлении значения');
        },
      },
    );
  };

  if (items.length === 0) {
    return <div className="text-muted-foreground text-sm">Нет товаров</div>;
  }

  const itemsWithBooking = items.filter(
    (item) => item.metadata && !item.metadata.isAccommodation && (item.metadata.adults !== undefined || item.metadata.accommodationType),
  );

  const handleSavePrice = async (itemId: number) => {
    const price = parseFloat(editPrice);
    if (isNaN(price) || price < 0) { return; }

    await updateCustom({
      url: `/orders/${r.id}/items/${itemId}`,
      method: 'patch',
      values: { unitPrice: price },
    });
    setEditingItemId(null);
    onRefresh?.();
  };

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Тип</TableHead>
            <TableHead>Название</TableHead>
            <TableHead>Тариф</TableHead>
            <TableHead className="text-right">Цена за ед.</TableHead>
            <TableHead className="text-right">Кол-во</TableHead>
            <TableHead className="text-right">Итого</TableHead>
            {hasBonusItems && <TableHead className="text-right">Действия</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const isAccommodation = item.metadata?.isAccommodation === true;
            const isBonus = item.targetType === 'BONUS';
            const bonusMeta = isBonus ? (item.metadata as { bonusName?: string; bonusType?: string; initialValue?: unknown; currentValue?: unknown; previousValue?: unknown } | null) : null;
            const itemName = isAccommodation
              ? `🏨 ${item.metadata?.accommodationName ?? 'Размещение'}`
              : isBonus
                ? (bonusMeta?.bonusName ?? `Бонус #${String(item.targetId)}`)
                : (item.target?.name ?? `#${String(item.targetId)}`);
            const typeLabel = isAccommodation
              ? 'Размещение'
              : (TARGET_TYPE_LABELS[item.targetType] ?? item.targetType);

            return (
              <TableRow key={item.id}>
                <TableCell>
                  <Badge variant={isAccommodation ? 'secondary' : 'outline'} className="text-[11px] px-1.5 py-0">
                    {typeLabel}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">
                  <div>{itemName}</div>
                  {isBonus && bonusMeta && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      По тарифу: {String(bonusMeta.initialValue)} → Осталось: {String(bonusMeta.currentValue)}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {item.pricingTierInfo ? (
                    <PricingTierBadge
                      tier={toPricingTier(item.pricingTierInfo)}
                      index={0}
                    />
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {isBonus ? (
                    <span className="text-muted-foreground">—</span>
                  ) : editingItemId === item.id ? (
                    <div className="flex items-center justify-end gap-1">
                      <Input
                        type="number"
                        step="0.01"
                        value={editPrice}
                        onChange={(e) => setEditPrice(e.target.value)}
                        className="h-6 w-24 text-right text-xs"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { void handleSavePrice(item.id); }
                          if (e.key === 'Escape') { setEditingItemId(null); }
                        }}
                        autoFocus
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-500/10"
                        onClick={() => void handleSavePrice(item.id)}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-muted"
                        onClick={() => setEditingItemId(null)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setEditingItemId(item.id); setEditPrice(item.unitPrice); }}
                      className="inline-flex items-center gap-1 cursor-pointer hover:underline group"
                      title="Нажмите чтобы изменить цену"
                    >
                      {formatCurrency(item.unitPrice, r.currency)}
                      <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  )}
                </TableCell>
                <TableCell className="text-right text-xs">{isBonus ? '—' : item.quantity}</TableCell>
                <TableCell className="text-right font-mono text-xs font-medium">
                  {isBonus ? <span className="text-muted-foreground">—</span> : formatCurrency(item.total, r.currency)}
                </TableCell>
                {hasBonusItems && (
                  <TableCell className="text-right">
                    {isBonus && bonusMeta ? (
                      <div className="flex items-center justify-end gap-1">
                        {bonusMeta.bonusType === 'NUMBER' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleBonusQuickAction(item.targetId, 'increment')}
                              disabled={bonusActionPending}
                              title="Увеличить на 1"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleBonusQuickAction(item.targetId, 'decrement')}
                              disabled={bonusActionPending}
                              title="Уменьшить на 1"
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {bonusMeta.bonusType === 'BOOLEAN' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const apiUrl = resolveApiUrl();
                              const currentBool = bonusMeta.currentValue as boolean;
                              bonusAction(
                                {
                                  url: `${apiUrl}/user-bonuses/admin/${item.targetId}`,
                                  method: 'patch',
                                  values: {
                                    action: 'set',
                                    value: !currentBool,
                                    idempotencyKey: crypto.randomUUID(),
                                  },
                                },
                                {
                                  onSuccess: () => { onRefresh?.(); notify.success('Действие выполнено'); },
                                  onError: () => { notify.error('Ошибка при выполнении действия'); },
                                },
                              );
                            }}
                            disabled={bonusActionPending}
                            title="Переключить"
                          >
                            <ToggleLeft className="h-3 w-3" />
                          </Button>
                        )}
                        {(bonusMeta.bonusType === 'NUMBER' || bonusMeta.bonusType === 'STRING') && (
                          editingBonusId === item.targetId ? (
                            <div className="flex items-center gap-1">
                              <Input
                                type={bonusMeta.bonusType === 'NUMBER' ? 'number' : 'text'}
                                value={editBonusValue}
                                onChange={(e) => setEditBonusValue(e.target.value)}
                                className="h-6 w-20 text-right text-xs"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { handleBonusSetValue(item.targetId, bonusMeta.bonusType ?? ''); }
                                  if (e.key === 'Escape') { setEditingBonusId(null); }
                                }}
                                autoFocus
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-green-600"
                                onClick={() => handleBonusSetValue(item.targetId, bonusMeta.bonusType ?? '')}
                              >
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => setEditingBonusId(null)}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setEditingBonusId(item.targetId); setEditBonusValue(String(bonusMeta.currentValue ?? '')); }}
                              disabled={bonusActionPending}
                              title="Изменить значение"
                            >
                              <Edit3 className="h-3 w-3" />
                            </Button>
                          )
                        )}
                      </div>
                    ) : null}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {items.map((item) => (
        item.pricingTierInfo ? <PricingTierInfoDisplay key={`tier-${item.id}`} tier={item.pricingTierInfo} /> : null
      ))}

      {itemsWithBooking.map((item) => (
        <BookingMetadataDisplay key={item.id} metadata={item.metadata!} />
      ))}
    </div>
  );
}

interface PaymentRecord {
  id: number;
  orderId: number;
  amount: string;
  currency: string;
  status: string;
  provider: string;
  providerPaymentId: string | null;
  paidAt: string | null;
  createdAt: string;
}

const PROVIDER_STYLE: Record<string, string> = {
  CLICK: 'bg-blue-500/10 text-blue-700 border-blue-500/20',
  PAYME: 'bg-cyan-500/10 text-cyan-700 border-cyan-500/20',
  STRIPE: 'bg-violet-500/10 text-violet-700 border-violet-500/20',
  MANUAL: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

export function OrderPaymentsSection({ record }: { record: OrderSectionRecord }) {
  const { result } = useList<PaymentRecord>({
    resource: 'payments',
    filters: [{ field: 'orderId', operator: 'eq', value: record.id }],
    pagination: { pageSize: 50 },
  });
  const go = useGo();

  const payments = result?.data ?? [];

  if (payments.length === 0) {
    return <div className="text-muted-foreground text-sm">Нет платежей</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>ID</TableHead>
          <TableHead>Сумма</TableHead>
          <TableHead>Статус</TableHead>
          <TableHead>Провайдер</TableHead>
          <TableHead>Дата оплаты</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {payments.map((p) => (
          <TableRow key={p.id} className="cursor-pointer" onClick={() => go({ to: `/payments/show/${p.id}` })}>
            <TableCell className="font-mono text-xs">#{p.id}</TableCell>
            <TableCell className="font-mono text-xs">{formatCurrency(p.amount, p.currency)}</TableCell>
            <TableCell>{renderStatus(p.status, PAYMENT_STATUS_MAP)}</TableCell>
            <TableCell>
              <Badge variant="outline" className={cn('text-[11px] px-1.5 py-0', PROVIDER_STYLE[p.provider.toUpperCase()])}>
                {p.provider}
              </Badge>
            </TableCell>
            <TableCell className="text-xs">{p.paidAt ? formatDate(p.paidAt) : <EmptyValue />}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function OrderHistorySection({ record }: { record: OrderSectionRecord }) {
  return <OrderHistory orderId={record.id} />;
}
